import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AppUpdatedPage } from '@/pages/error/AppUpdatedPage';
import { isChunkLoadError } from '@/lib/lazy-with-reload';
import { captureError } from '@/lib/sentry';

// ErrorBoundary global. React requiere que sea class component porque
// `componentDidCatch` y `getDerivedStateFromError` solo existen en clases.

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Reporta a Sentry si está habilitado; loguea en consola para dev.
    // El chunk que se llevó el deploy queda fuera: pasa en cada release con
    // pestañas viejas abiertas, es esperado y solo haría ruido en Sentry.
    if (!isChunkLoadError(error)) {
      captureError(error, { componentStack: info.componentStack });
    }
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] Captured error:', error, info);
    }
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    // Mismo mensaje que el `errorElement` del router: la app se actualizó,
    // recargar alcanza. No es un "algo salió mal".
    if (isChunkLoadError(this.state.error)) {
      return <AppUpdatedPage detail={this.state.error?.message} />;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Algo salió mal</CardTitle>
            <CardDescription>
              Ocurrió un error inesperado en la aplicación. Si el problema persiste,
              contacta al equipo de soporte.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {this.state.error?.message ? (
              <p className="rounded-md border border-border bg-surface-2 p-3 font-mono text-xs text-text-muted">
                {this.state.error.message}
              </p>
            ) : null}
            <Button type="button" onClick={this.handleReload}>
              Recargar página
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}
